import {
  distanceM, projeterSurSegment, segments,
  type Anneau, type Commande, type Figure, type Point, type Sommet,
} from './edition-geometrie.js';
import { appliquer, inverser } from './edition-geometrie.js';

/**
 * L'ÉDITION TOPOLOGIQUE PARTAGÉE — déplacer une borne mitoyenne déplace LES
 * DEUX fonds.
 *
 * ⚠️⚠️ CE QUE SON ABSENCE COÛTE, ET POURQUOI RIEN NE LE SIGNALE. Sans elle,
 * corriger le contour d'une parcelle laisse sa voisine où elle était : entre
 * les deux s'ouvre un INTERSTICE — quelques centimètres de sol qui
 * n'appartiennent plus à personne. Et **la garde de recouvrement ne peut pas
 * le voir** : elle refuse qu'un fonds en morde un autre, elle ne dit rien d'un
 * vide. Un chevauchement se détecte ; un trou se constate des années plus
 * tard, chez le géomètre appelé pour un litige de bornage.
 *
 * C'est la capacité centrale d'ArcGIS Parcel Fabric, et c'est elle qui sépare
 * un éditeur de dessin d'un atelier cadastral.
 *
 * ⚠️⚠️ LE PARTAGE SE DÉTECTE, IL NE SE DÉCLARE PAS — décision structurante.
 * Le réflexe serait une table `arete_partagee` : chaque côté saurait qui le
 * borde. Ce serait juste sur un registre bâti pour ça, et inapplicable ici :
 * les 2,7 millions de parcelles ont été déposées INDÉPENDAMMENT, levé par levé,
 * import par import. Aucune ne sait qui la touche. Exiger une topologie
 * déclarée reviendrait à refaire le stock avant de pouvoir corriger la première
 * limite. On détecte donc la coïncidence AU MOMENT DE L'ÉDITION, sur les
 * voisines chargées — ce qui marche sur l'existant, dès aujourd'hui.
 *
 * ⚠️ LA TOLÉRANCE EST EN MÈTRES, ET ELLE EST NÉCESSAIRE. Deux bornes « au même
 * endroit » relevées par deux géomètres à dix ans d'écart diffèrent de quelques
 * centimètres. Une égalité stricte ne trouverait jamais de sommet partagé, et
 * la fonctionnalité serait inerte tout en paraissant marcher.
 *
 * ⚠️⚠️ ET APRÈS LE DÉPLACEMENT, LES SOMMETS SONT EXACTEMENT ÉGAUX. Les replacer
 * « chacun à sa nouvelle position, à la tolérance près » laisserait un
 * interstice à chaque correction : plus petit, jamais nul, et qui se rouvre au
 * geste suivant. On écrit LE MÊME point dans tous.
 */

/** Où vit un sommet : quelle figure, quel anneau, quel sommet. */
export interface Designation {
  readonly figureId: string;
  readonly anneauId: string;
  readonly sommetId: string;
}

/**
 * UN LOT — plusieurs commandes qui ne font qu'un seul geste.
 *
 * ⚠️ L'HISTORIQUE PORTE DES LOTS, PAS DES COMMANDES. Un déplacement mitoyen
 * touche deux figures ; si l'historique gardait deux entrées, une annulation
 * remettrait UNE parcelle en place et laisserait l'autre déplacée — elle
 * FABRIQUERAIT l'interstice qu'on cherche à éviter, et par le geste censé
 * réparer.
 */
export interface Lot {
  readonly libelle: string;
  readonly gestes: ReadonlyArray<{ readonly figureId: string; readonly commande: Commande }>;
}

export interface Chantier {
  readonly figures: Map<string, Figure>;
  passe: Lot[];
  futur: Lot[];
}

export function ouvrirChantier(figures: Record<string, Figure>): Chantier {
  return { figures: new Map(Object.entries(figures)), passe: [], futur: [] };
}

function sommetsDe(f: Figure): Array<{ anneau: Anneau; sommet: Sommet }> {
  return f.anneaux.flatMap((a) => a.sommets.map((s) => ({ anneau: a, sommet: s })));
}

/**
 * Tous les sommets du chantier qui occupent le MÊME point de terrain que `p`.
 *
 * ⚠️ RENDU TRIÉ PAR DISTANCE, ET LE PLUS PROCHE D'ABORD. L'écran doit pouvoir
 * nommer « le sommet » sans ambiguïté quand il en désigne un.
 */
export function sommetsCoincidents(
  ch: Chantier, p: Point, toleranceM: number,
): Designation[] {
  const trouves: Array<{ d: Designation; dist: number }> = [];
  for (const [figureId, f] of ch.figures) {
    for (const { anneau, sommet } of sommetsDe(f)) {
      const dist = distanceM(p, sommet);
      if (dist <= toleranceM) {
        trouves.push({ d: { figureId, anneauId: anneau.id, sommetId: sommet.id }, dist });
      }
    }
  }
  return trouves.sort((a, b) => a.dist - b.dist).map((t) => t.d);
}

/**
 * LES SOMMETS VOISINS QUI REPOSENT SUR UN CÔTÉ QUE CE DÉPLACEMENT VA BOUGER.
 *
 * ⚠️⚠️ C'EST LE CAS QUI FABRIQUE LES INTERSTICES, ET IL NE SE RÉSOUT PAS TOUT
 * SEUL. Ma parcelle a deux sommets, à 0 m et 10 m d'un côté. Ma voisine en a
 * TROIS sur la même ligne : 0, 5 et 10. Nos sommets à 0 et 10 coïncident, et
 * seront déplacés ensemble — mais le sien à 5 reste en arrière, et le sol
 * s'ouvre en biseau entre les deux tracés.
 *
 * ⚠️ ON NE LE « CORRIGE » PAS EN DÉPLAÇANT CE SOMMET AU PRORATA. Ce serait
 * inventer une position que ni le titre ni le levé ne portent — et la même
 * faute que réécrire une géométrie d'après un polygone plutôt que d'après la
 * mesure de l'acte. On le DÉSIGNE, pour que l'écran refuse ou avertisse. Un
 * atelier qui ne sait pas résoudre un cas doit le nommer, jamais le taire.
 */
export function sommetsSurCotesDeplaces(
  ch: Chantier, deplaces: readonly Designation[], toleranceM: number,
): Designation[] {
  const dansLeLot = new Set(deplaces.map((d) => `${d.figureId}|${d.anneauId}|${d.sommetId}`));
  const cotes: Array<{ a: Point; b: Point }> = [];

  // Les côtés qui touchent un sommet déplacé — ce sont eux qui bougeront.
  for (const d of deplaces) {
    const f = ch.figures.get(d.figureId);
    if (!f) continue;
    for (const seg of segments(f)) {
      if (seg.a.id === d.sommetId || seg.b.id === d.sommetId) cotes.push({ a: seg.a, b: seg.b });
    }
  }

  const risques: Designation[] = [];
  for (const [figureId, f] of ch.figures) {
    for (const { anneau, sommet } of sommetsDe(f)) {
      const cle = `${figureId}|${anneau.id}|${sommet.id}`;
      if (dansLeLot.has(cle)) continue;
      for (const c of cotes) {
        const pr = projeterSurSegment(sommet, c.a, c.b);
        /* ⚠️ `t` STRICTEMENT ENTRE 0 ET 1 : un sommet projeté SUR une extrémité
           n'est pas « posé sur le côté », c'est un sommet coïncident — et
           celui-là est déjà traité, ou trop loin pour l'être. Sans ce garde,
           chaque déplacement se signalerait lui-même comme un risque. */
        if (pr.distanceM <= toleranceM && pr.t > 0.001 && pr.t < 0.999) {
          risques.push({ figureId, anneauId: anneau.id, sommetId: sommet.id });
          break;
        }
      }
    }
  }
  return risques;
}

export interface DeplacementPartage {
  readonly lot: Lot;
  /** Tous les sommets que ce geste déplace — au moins celui qu'on a saisi. */
  readonly deplaces: readonly Designation[];
  /** Ceux qui resteront en arrière et ouvriront un biseau. Vide = sol continu. */
  readonly risquesInterstice: readonly Designation[];
}

/**
 * Prépare le déplacement d'un sommet ET de tous ceux qui l'accompagnent.
 *
 * ⚠️ IL PRÉPARE, IL N'APPLIQUE PAS. L'écran doit pouvoir montrer ce que le
 * geste va emporter — « 2 parcelles, 1 sommet resterait en arrière » — AVANT
 * que le sol ne bouge. Un geste qui s'exécute puis s'explique arrive trop tard.
 */
export function preparerDeplacementPartage(
  ch: Chantier, saisi: Designation, vers: Point, toleranceM: number,
): DeplacementPartage {
  const f = ch.figures.get(saisi.figureId);
  if (!f) throw new Error(`Figure ${saisi.figureId} absente du chantier.`);
  const a = f.anneaux.find((x) => x.id === saisi.anneauId);
  const s = a?.sommets.find((x) => x.id === saisi.sommetId);
  if (!a || !s) throw new Error(`Sommet ${saisi.sommetId} absent du chantier.`);

  const deplaces = sommetsCoincidents(ch, s, toleranceM);
  const gestes = deplaces.map((d) => {
    const fd = ch.figures.get(d.figureId)!;
    const ad = fd.anneaux.find((x) => x.id === d.anneauId)!;
    const sd = ad.sommets.find((x) => x.id === d.sommetId)!;
    return {
      figureId: d.figureId,
      commande: {
        type: 'deplacer' as const, anneauId: d.anneauId, sommetId: d.sommetId,
        avant: { lon: sd.lon, lat: sd.lat },
        /* ⚠️ LE MÊME POINT POUR TOUS, à la virgule près. « Chacun à sa
           nouvelle position » laisserait un interstice résiduel à chaque
           correction : plus petit, jamais nul. */
        apres: { lon: vers.lon, lat: vers.lat },
      },
    };
  });

  return {
    lot: {
      libelle: deplaces.length > 1
        ? `Déplacer un sommet mitoyen (${new Set(deplaces.map((d) => d.figureId)).size} fonds)`
        : 'Déplacer un sommet',
      gestes,
    },
    deplaces,
    risquesInterstice: sommetsSurCotesDeplaces(ch, deplaces, toleranceM),
  };
}

/**
 * Applique un lot — TOUT ou RIEN.
 *
 * ⚠️ ON VÉRIFIE AVANT D'ÉCRIRE. `appliquer` lève si un anneau a disparu ; à
 * mi-lot, la première figure serait déjà déplacée et la seconde non —
 * exactement l'interstice qu'on évite. Même raison que le `SAVEPOINT` du lot de
 * terrain, à un étage au-dessus.
 */
export function executerLot(ch: Chantier, lot: Lot): void {
  for (const g of lot.gestes) {
    const f = ch.figures.get(g.figureId);
    if (!f) throw new Error(`Figure ${g.figureId} absente : le lot n'est pas applicable.`);
    const c = g.commande;
    if ('anneauId' in c && !f.anneaux.some((x) => x.id === c.anneauId)) {
      throw new Error(`Anneau ${c.anneauId} absent de ${g.figureId} : le lot n'est pas applicable.`);
    }
  }
  for (const g of lot.gestes) appliquer(ch.figures.get(g.figureId)!, g.commande);
  ch.passe.push(lot);
  ch.futur.length = 0;
}

export function peutAnnulerLot(ch: Chantier): boolean { return ch.passe.length > 0; }
export function peutRefaireLot(ch: Chantier): boolean { return ch.futur.length > 0; }

export function annulerLot(ch: Chantier): Lot | null {
  const lot = ch.passe.pop();
  if (!lot) return null;
  /* ⚠️ EN ORDRE INVERSE. Sur des déplacements indépendants l'ordre est sans
     effet ; il cesse de l'être dès qu'un lot mêle insertions et suppressions,
     où un index dépend de ce qui précède. On inverse toujours. */
  for (let i = lot.gestes.length - 1; i >= 0; i -= 1) {
    const g = lot.gestes[i]!;
    appliquer(ch.figures.get(g.figureId)!, inverser(g.commande));
  }
  ch.futur.push(lot);
  return lot;
}

export function refaireLot(ch: Chantier): Lot | null {
  const lot = ch.futur.pop();
  if (!lot) return null;
  for (const g of lot.gestes) appliquer(ch.figures.get(g.figureId)!, g.commande);
  ch.passe.push(lot);
  return lot;
}

/**
 * L'ÉCART MAXIMAL entre deux figures là où elles se touchent — le témoin du sol
 * continu.
 *
 * ⚠️ RENDU EN MÈTRES, ET `null` QUAND ELLES NE SE TOUCHENT PAS. Zéro dirait
 * « elles se touchent parfaitement » là où la vérité est « elles ne se touchent
 * pas du tout » : les deux se ressemblent, et une seule est rassurante.
 */
export function ecartAuContact(
  ch: Chantier, aId: string, bId: string, toleranceM: number,
): number | null {
  const fa = ch.figures.get(aId); const fb = ch.figures.get(bId);
  if (!fa || !fb) return null;
  let pire: number | null = null;
  for (const { sommet } of sommetsDe(fa)) {
    let mini = Infinity;
    for (const seg of segments(fb)) {
      mini = Math.min(mini, projeterSurSegment(sommet, seg.a, seg.b).distanceM);
    }
    if (mini <= toleranceM) pire = Math.max(pire ?? 0, mini);
  }
  return pire;
}
